import React, { type MouseEventHandler } from 'react';
import cx from 'classnames';
import {
  Checkmark,
  CircleCompass,
  CircleQuestion,
  Play,
} from '@design-systems/icons';
import useWinkBreakpoints from '@mc/hooks/useWinkBreakpoints';
import Heading from '../Heading';
import Text from '../Text';
import ClusterLayout from '../ClusterLayout';
import Button, { type ButtonProps } from '../Button';
import { ListItem } from '../List';
import stylesheet from './EmptyStates.css';

type PonIconType = 'Work Assist' | 'Video' | 'Guide';

export type EmptyStateProps = {
  /** Size of the empty state */
  size?: 'medium' | 'large';
  /** Orientation of the empty state */
  orientation?: 'horizontal' | 'vertical';
  /** Title of the empty state */
  title: string;
  /** Subtitle of the empty state */
  subtitle?: string;
  /** When actions are provided, it will not render the primary and secondary button */
  actions?: React.ReactNode;
  /** Primary call to action label */
  primaryCtaLabel?: string;
  /** Secondary call to action label */
  secondaryCtaLabel?: string;
  /** Primary CTA button props */
  primaryCtaProps?: Omit<ButtonProps, 'type'>;
  /** Secondary CTA button props */
  secondaryCtaProps?: Omit<ButtonProps, 'type'>;
  /** Image source */
  imgSrc?: string;
  /** Image alt text */
  imageAlt?: string;
  /** Bullets list to display supporting benefits of feature activation */
  bulletLists?: string[];
  /** additional text content */
  additionalContent?: string;
  /** legal extra text content */
  legalContent?: string;
  /** Point of need button. render your own pon button */
  ponButton?: React.ReactNode;
  /** Point of need button label */
  ponButtonLabel?: string;
  /** Point of need button onClick handler */
  ponButtonOnClick?: MouseEventHandler<HTMLElement>;
  /** Point of need button href */
  ponButtonHref?: string;
  /** Point of need button icon */
  ponIconType?: PonIconType;
  /** a custom classname that is appended to empty states container */
  className?: string;
  /** Dark mode filter type for the image */
  darkModeFilter?: 'none' | 'invert' | 'maintain-cavendish';
};

const EmptyStates = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  function EmptyStates(
    {
      size = 'large',
      orientation = 'horizontal',
      title,
      subtitle,
      actions = undefined,
      primaryCtaLabel,
      secondaryCtaLabel,
      primaryCtaProps = {},
      secondaryCtaProps = {},
      additionalContent,
      legalContent,
      imgSrc,
      imageAlt = 'Empty state illustration',
      ponButton,
      ponButtonLabel,
      ponButtonOnClick = undefined,
      ponButtonHref = '',
      ponIconType = 'Work Assistant',
      bulletLists,
      className = '',
      darkModeFilter = 'none',
    },
    forwardedRef,
  ) {
    if (size === 'large' && orientation === 'vertical') {
      throw new Error(
        'EmptyStates component does not support large size with vertical orientation',
      );
    }

    if (orientation === 'vertical' && bulletLists) {
      throw new Error(
        'EmptyStates component does not support bullet lists with vertical orientation',
      );
    }

    let ponIcon = <CircleQuestion width={16} height={16} />;
    if (ponIconType === 'Video') {
      ponIcon = <Play width={16} height={16} />;
    } else if (ponIconType === 'Guide') {
      ponIcon = <CircleCompass width={16} height={16} />;
    }

    const { isTablet, isPhone } = useWinkBreakpoints();
    const responsiveOrientation =
      isTablet || isPhone ? 'vertical' : orientation;
    const responsiveSize = isTablet || isPhone ? 'medium' : size;

    return (
      <div
        className={cx(
          stylesheet.root,
          stylesheet[responsiveSize],
          {
            [stylesheet.horizontal]: responsiveOrientation === 'horizontal',
            [stylesheet.vertical]: responsiveOrientation === 'vertical',
          },
          className,
        )}
        ref={forwardedRef}
      >
        <div
          className={cx(stylesheet.leftSlot, {
            [stylesheet.centered]: responsiveOrientation === 'vertical',
          })}
        >
          <div className={cx(stylesheet.heading)}>
            <Heading
              level={
                responsiveSize === 'large' &&
                responsiveOrientation === 'horizontal'
                  ? 3
                  : 4
              }
            >
              {title}
            </Heading>
            <Text appearance="small-secondary">{subtitle}</Text>
          </div>
          {/* Bullet lists are not allowed for vertical layout */}
          {responsiveOrientation === 'horizontal' && bulletLists && (
            <div>
              {bulletLists.map((bullet: string, index: number) => (
                <ListItem key={index} appearance="small-secondary">
                  <Checkmark width={16} height={16} />
                  <Text appearance="small-secondary">{bullet}</Text>
                </ListItem>
              ))}
            </div>
          )}
          {additionalContent && (
            <Text appearance="small-secondary">{additionalContent}</Text>
          )}
          {actions
            ? actions
            : (primaryCtaLabel || secondaryCtaLabel) && (
                <ClusterLayout gap={2}>
                  {primaryCtaLabel && (
                    <Button {...primaryCtaProps} type="primary" size="large">
                      {primaryCtaLabel}
                    </Button>
                  )}
                  {secondaryCtaLabel && (
                    <Button
                      {...secondaryCtaProps}
                      type="secondary"
                      size="large"
                    >
                      {secondaryCtaLabel}
                    </Button>
                  )}
                </ClusterLayout>
              )}
          {legalContent && (
            <Text appearance="tiny-secondary">{legalContent}</Text>
          )}
          {ponButton
            ? ponButton
            : ponButtonLabel && (
                <ClusterLayout>
                  <Button
                    type="plain"
                    onClick={ponButtonOnClick}
                    className={stylesheet.ponButton}
                    href={ponButtonHref}
                    target="_blank"
                  >
                    {ponIcon}
                    {ponButtonLabel}
                  </Button>
                </ClusterLayout>
              )}
        </div>
        {imgSrc && (
          <img
            className={cx(stylesheet.rightSlot, {
              'mcds-dark-mode-invertable': darkModeFilter === 'invert',
              'mcds-dark-mode-maintain-cavendish':
                darkModeFilter === 'maintain-cavendish',
            })}
            src={imgSrc}
            alt={imageAlt}
          />
        )}
      </div>
    );
  },
);

export default EmptyStates;
